/**
 * CourseView.js — Premium global-grade learning experience.
 * --------------------------------------------------------------------
 * Sprint LMS-X upgrade.
 *
 * KEEPS INTACT:
 *  - /api/lms/courses/{course_id}/journey        (course meta + modules)
 *  - /api/lms/lessons/{lesson_id}/access         (gated stream)
 *  - /api/lms/watch/start | /ping | /complete    (watch tracking)
 *  - Existing LMS gating + unique-seconds-watched logic
 *
 * UPGRADES:
 *  - Cinematic navy + gold hero with progress + breadcrumb
 *  - Glass cards, subtle elevations, premium tone
 *  - Watermarked video player retained
 *  - Sidebar lesson tree with locked-state and progress
 *  - "Premium Coach" CTAs into the MUN Prep Hub
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, Lock, PlayCircle, Sparkles, AlertCircle,
  GraduationCap, ScrollText, Mic2, Trophy, ArrowRight, Crown,
} from "lucide-react";

const DEFAULT_COURSE_ID = "course-mun-101";
const HEARTBEAT_MS = 5000;

export default function CourseView() {
  const courseId = DEFAULT_COURSE_ID;
  const [journey, setJourney] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [lockedPreview, setLockedPreview] = useState(null);
  const [lessonAccess, setLessonAccess] = useState(null);
  const [loadingLesson, setLoadingLesson] = useState(false);

  const videoRef = useRef(null);
  const lastPingPosRef = useRef(0);
  const lastTimeUpdateRef = useRef(0);
  const intervalIdRef = useRef(null);

  const reloadJourney = async () => {
    try {
      const { data } = await api.get(`/lms/courses/${courseId}/journey`);
      setJourney(data);
      if (!activeLessonId) {
        for (const m of data.modules) {
          const first = m.lessons.find((l) => !l.locked);
          if (first) {
            setActiveLessonId(first.lesson_id);
            return;
          }
        }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load course");
    }
  };

  useEffect(() => {
    reloadJourney();
    return () => { if (intervalIdRef.current) clearInterval(intervalIdRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeLessonId) return;
    setLoadingLesson(true);
    setLessonAccess(null);
    (async () => {
      try {
        const { data } = await api.get(`/lms/lessons/${activeLessonId}/access`);
        setLessonAccess(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Lesson unavailable");
      } finally { setLoadingLesson(false); }
    })();
  }, [activeLessonId]);

  useEffect(() => {
    if (!lessonAccess?.video_url || lessonAccess.locked) return;
    let cancelled = false;
    (async () => {
      try {
        await api.post("/lms/watch/start", {
          lesson_id: lessonAccess.lesson_id,
          total_seconds: videoRef.current?.duration || 0,
        });
      } catch { /* non-fatal */ }
      if (cancelled) return;
      lastPingPosRef.current = lessonAccess.last_position_seconds || 0;
      lastTimeUpdateRef.current = lastPingPosRef.current;
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
      intervalIdRef.current = setInterval(sendHeartbeat, HEARTBEAT_MS);
    })();
    return () => {
      cancelled = true;
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonAccess?.video_url, lessonAccess?.locked, lessonAccess?.lesson_id]);

  const sendHeartbeat = async () => {
    const v = videoRef.current;
    if (!v || v.paused || !lessonAccess) return;
    const cur = v.currentTime || 0;
    const total = v.duration || 0;
    const from = Math.max(0, lastPingPosRef.current);
    const to = Math.max(from, cur);
    if (to <= from) return;
    lastPingPosRef.current = cur;
    try {
      const { data } = await api.post("/lms/watch/ping", {
        lesson_id: lessonAccess.lesson_id,
        from_seconds: from, to_seconds: to,
        position_seconds: cur, total_seconds: total,
      });
      setLessonAccess((prev) => prev && { ...prev, watch_percent: data.watch_percent });
      if (data.watched_enough) reloadJourney();
    } catch { /* network jitter */ }
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    const cur = v.currentTime || 0;
    if (Math.abs(cur - lastTimeUpdateRef.current) > 2) lastPingPosRef.current = cur;
    lastTimeUpdateRef.current = cur;
  };

  const onEnded = async () => {
    if (!lessonAccess) return;
    await sendHeartbeat();
    try {
      await api.post("/lms/watch/complete", {
        lesson_id: lessonAccess.lesson_id,
        position_seconds: videoRef.current?.duration || 0,
        total_seconds: videoRef.current?.duration || 0,
      });
      toast.success("Lesson complete — next lesson unlocked");
      reloadJourney();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const activeMeta = useMemo(() => {
    if (!journey) return null;
    for (const m of journey.modules) {
      const l = m.lessons.find((x) => x.lesson_id === activeLessonId);
      if (l) return { module: m, lesson: l };
    }
    return null;
  }, [journey, activeLessonId]);

  if (!journey) {
    return (
      <DashboardLayout>
        <p className="text-sm text-slate-500" data-testid="course-loading">Loading course…</p>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Course Library">
      <div data-testid="course-view-root" className="space-y-6 pb-12">
        {/* Hero */}
        <section
          data-testid="course-hero"
          className="relative overflow-hidden rounded-3xl border border-[#0F4C81] bg-gradient-to-br from-[#0A192F] via-[#0F4C81] to-[#0A192F] p-6 md:p-7 shadow-2xl text-white"
        >
          <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-[#0F4C81]/40 blur-3xl" />
          <div className="relative">
            <Link
              to="/dashboard/lms"
              data-testid="course-back-academy"
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-white"
            >
              ← Learning Academy
            </Link>
            <p className="mt-3 text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold flex items-center gap-2">
              <GraduationCap size={12} /> Diplomexa Course Library
            </p>
            <h1
              className="mt-1 text-3xl md:text-4xl font-bold leading-tight tracking-tight"
              data-testid="course-title"
            >
              {journey.course.title}
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-blue-100">{journey.course.description}</p>
            <div className="mt-4 max-w-xl">
              <div className="flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1 font-semibold text-[#D4AF37]" data-testid="course-progress-text">
                  <Sparkles size={11} /> {journey.completed_lessons}/{journey.total_lessons} lessons · {journey.overall_percent}%
                </span>
                <span className="text-blue-100">Premium content protected · {`Diplomexa LMS`}</span>
              </div>
              <div className="mt-1.5 h-2 rounded-full bg-white/15 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] transition-all"
                  style={{ width: `${journey.overall_percent}%` }}
                  data-testid="course-progress-bar"
                />
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                to="/dashboard/lms/mun-prep?tab=paper"
                data-testid="course-cta-paper"
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#D4AF37] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A192F] hover:bg-[#c69f30]"
              >
                <ScrollText size={11} /> Get paper review
              </Link>
              <Link
                to="/dashboard/lms/mun-prep?tab=speech"
                data-testid="course-cta-speech"
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#D4AF37]/40 bg-white/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-white/20"
              >
                <Mic2 size={11} /> Practise speech
              </Link>
              <Link
                to="/dashboard/lms/mun-prep?tab=report"
                data-testid="course-cta-report"
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#D4AF37]/40 bg-white/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-white/20"
              >
                <Crown size={11} /> Readiness report
              </Link>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Sidebar lessons */}
          <aside className="lg:col-span-4 space-y-3">
            {journey.modules.map((m) => (
              <div
                key={m.id}
                className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm"
                data-testid={`module-${m.id}`}
              >
                <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-slate-100/80 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold">
                      Module {m.order}
                    </p>
                    <p className="text-sm font-semibold text-[#0A192F]">{m.title}</p>
                  </div>
                  {m.completed && (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" data-testid={`module-${m.id}-complete`} />
                  )}
                </div>
                <div className="p-2 space-y-1">
                  {m.lessons.map((l) => {
                    const isActive = l.lesson_id === activeLessonId;
                    const Icon = l.completed ? CheckCircle2 : l.locked ? Lock : PlayCircle;
                    return (
                      <button
                        key={l.lesson_id}
                        type="button"
                        onClick={() => {
                          if (l.locked) {
                            setLockedPreview({
                              lesson_id: l.lesson_id, title: l.title,
                              reason: l.lock_reason || "Lesson is locked",
                            });
                            toast.warning(l.lock_reason || "Lesson is locked");
                            return;
                          }
                          setLockedPreview(null);
                          setActiveLessonId(l.lesson_id);
                        }}
                        title={l.locked ? l.lock_reason : ""}
                        className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm flex items-center gap-3 transition-all ${
                          isActive
                            ? "bg-[#0A192F] text-white border-[#D4AF37] shadow-md"
                            : l.locked
                            ? "bg-slate-50 text-slate-500 border-transparent cursor-not-allowed"
                            : "bg-white text-[#0A192F] border-slate-200 hover:border-[#D4AF37]/60 hover:-translate-y-0.5"
                        }`}
                        data-testid={`lesson-${l.lesson_id}-button`}
                      >
                        <Icon className={`h-4 w-4 flex-shrink-0 ${l.completed ? "text-emerald-500" : l.locked ? "text-slate-400" : isActive ? "text-[#D4AF37]" : "text-[#0F4C81]"}`} />
                        <span className="flex-1 font-medium truncate">{l.title}</span>
                        <span className="text-[10px] uppercase tracking-wider opacity-70">
                          {l.completed ? "Done" : l.locked ? "Locked" : `${l.watch_percent || 0}%`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </aside>

          {/* Player */}
          <main className="lg:col-span-8 space-y-4">
            {loadingLesson && (
              <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500 shadow-sm">
                Loading lesson…
              </div>
            )}

            {!loadingLesson && lockedPreview && (
              <div
                className="rounded-2xl border border-amber-300 bg-amber-50 p-8 flex flex-col items-center gap-3 shadow-sm"
                data-testid="lesson-locked-card"
              >
                <Lock className="h-8 w-8 text-amber-600" />
                <p className="text-lg font-semibold text-[#0A192F]">
                  "{lockedPreview.title}" is locked
                </p>
                <p className="text-sm text-amber-800 text-center max-w-md" data-testid="lesson-locked-reason">
                  {lockedPreview.reason}
                </p>
                <button
                  type="button"
                  onClick={() => setLockedPreview(null)}
                  className="mt-2 text-xs uppercase tracking-wider text-[#0F4C81] font-semibold"
                  data-testid="lesson-locked-dismiss"
                >
                  Back to current lesson
                </button>
              </div>
            )}

            {!loadingLesson && !lockedPreview && lessonAccess?.locked && (
              <div
                className="rounded-2xl border border-amber-300 bg-amber-50 p-8 flex flex-col items-center gap-3 shadow-sm"
                data-testid="lesson-locked-card"
              >
                <Lock className="h-8 w-8 text-amber-600" />
                <p className="text-lg font-semibold text-[#0A192F]">This lesson is locked</p>
                <p className="text-sm text-amber-800 text-center max-w-md" data-testid="lesson-locked-reason">
                  {lessonAccess.reason}
                </p>
              </div>
            )}

            {!loadingLesson && !lockedPreview && lessonAccess && !lessonAccess.locked && (
              <>
                <div
                  className="rounded-2xl overflow-hidden bg-[#0A192F] border border-[#D4AF37]/30 shadow-2xl"
                  data-testid="lesson-player-card"
                >
                  <div className="relative aspect-video bg-black">
                    <video
                      ref={videoRef}
                      src={lessonAccess.video_url}
                      className="w-full h-full"
                      controls
                      controlsList="nodownload"
                      onContextMenu={(e) => e.preventDefault()}
                      onTimeUpdate={onTimeUpdate}
                      onEnded={onEnded}
                      data-testid="lesson-video"
                    />
                    <div
                      className="pointer-events-none absolute top-3 right-3 px-2 py-1 rounded bg-black/40 text-white text-[10px] tracking-widest uppercase font-semibold"
                      data-testid="watermark"
                    >
                      Diplomexa LMS · {activeMeta?.lesson?.title?.slice(0, 30)}
                    </div>
                    {/* Diagonal watermark for low-contrast tamper resistance */}
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.06]">
                      <span className="-rotate-12 text-white text-3xl font-black uppercase tracking-[0.2em]">Diplomexa Academy</span>
                    </div>
                  </div>
                  <div className="p-6 text-white">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                      {activeMeta?.module?.title}
                    </p>
                    <h2 className="mt-2 text-2xl font-semibold tracking-tight" data-testid="lesson-title">
                      {lessonAccess.title}
                    </h2>
                    <div className="mt-4 flex items-center gap-3 text-xs">
                      <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] transition-all"
                          style={{ width: `${lessonAccess.watch_percent || 0}%` }}
                          data-testid="lesson-watch-bar"
                        />
                      </div>
                      <span className="text-slate-300" data-testid="lesson-watch-pct">
                        {lessonAccess.watch_percent || 0}% watched
                      </span>
                      <span className="text-[#D4AF37]">need {lessonAccess.required_watch_percent}%</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3 shadow-sm">
                  <AlertCircle className="h-4 w-4 text-[#0F4C81]" />
                  <p className="text-xs text-slate-600">
                    Watch progress is recorded based on{" "}
                    <span className="font-semibold text-[#0A192F]">unique seconds viewed</span>.
                    Skipping ahead won't count.
                  </p>
                </div>

                {/* Premium Coach CTA */}
                <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-amber-50 via-white to-white p-5 shadow-sm">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#0F4C81] text-[#D4AF37]">
                        <Crown size={16} />
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">
                          Diplomexa Readiness Coach
                        </p>
                        <p className="text-sm font-semibold text-[#0A192F]">
                          Get a Premium Position Paper Review on what you just learned
                        </p>
                      </div>
                    </div>
                    <Link
                      to="/dashboard/lms/mun-prep?tab=paper"
                      data-testid="course-coach-paper-cta"
                      className="inline-flex items-center gap-1 rounded-lg bg-[#0A192F] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-[#0F2C50]"
                    >
                      Open studio <ArrowRight size={11} />
                    </Link>
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </DashboardLayout>
  );
}
